import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PeopleAlt as TotalIcon,
  Person as AccountedIcon,
  PersonOff as NotAccountedIcon,
  Schedule as NotCheckedIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Send as SubmitIcon,
  AddCircle as AddedIcon,
  ChevronRight as ChevronRightIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  CheckCircle as CheckIcon,
  Wifi as WifiIcon,
} from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { IncidentSummary, IncidentRosterPerson, ActivityEntry, Incident } from '../Types/Incident';
import {
  getIncidentSummary,
  getIncidentDetail,
  getUnaccountedPeople,
  getIncidentActivity,
  createCheckpoint,
} from '../Services/IncidentService';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import KpiCard from '../Components/Shared/KpiCard';
import ResponsiveKpiGrid from '../Components/Shared/ResponsiveKpiGrid';
import StackedMiniKpi from '../Components/Shared/StackedMiniKpi';
import { isIncidentClosed, canEditClosedRoster } from '../Utils/incidentHelpers';
import { useOnlineStatus } from '../Hooks/useOnlineStatus';
import SyncStatusIndicator from '../Components/Elements/SyncStatusIndicator';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import { formatIncidentActivity } from '../Utils/activityHelpers';
import { deriveScopeCountsFromSummary } from '../features/incidents/accountabilityScope';

const ReviewSubmitPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const { hasPermission, hasAnyRole, security } = useSecurity();

  const canRecordCheckpoint = hasPermission('Checkpoint.Create');

  const [summary, setSummary] = useState<IncidentSummary | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [notAccountedPeople, setNotAccountedPeople] = useState<IncidentRosterPerson[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordSuccess, setRecordSuccess] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async (showSpinner = true) => {
    if (!incidentId) return;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [sumResult, detailResult, unaccResult, actResult] = await Promise.all([
        getIncidentSummary(incidentId).catch(() => null),
        getIncidentDetail(incidentId).catch(() => null),
        getUnaccountedPeople(incidentId).catch(() => ({ notAccounted: [], serverTimeUtc: undefined })),
        getIncidentActivity(incidentId).catch(() => ({ activity: [], serverTimeUtc: undefined })),
      ]);
      if (sumResult) setSummary(sumResult.summary);
      setIncident(detailResult);
      setNotAccountedPeople(unaccResult.notAccounted);
      setRecentActivity(actResult.activity);
      const serverTime = sumResult?.serverTimeUtc ?? actResult.serverTimeUtc ?? unaccResult.serverTimeUtc;
      if (serverTime) {
        setLastUpdated(new Date(serverTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const recordCheckpoint = async () => {
    if (!incidentId || recording) return;
    // Prevent recording for closed incidents
    if (isIncidentClosed({ status: displayStatus })) return;
    setRecording(true);
    setRecordError(null);
    try {
      await createCheckpoint(incidentId, {
        notes: `Progress checkpoint: ${accountedCount}/${total} accounted (accountability roster).`,
      });
      setRecordSuccess(true);
      loadData(false);
    } catch (err) {
      setRecordError(err instanceof Error ? err.message : 'Failed to record checkpoint.');
    } finally {
      setRecording(false);
    }
  };

  const handleRecordCheckpoint = () => {
    if (notAccountedCount > 0 || notCheckedCount > 0) {
      setConfirmOpen(true);
    } else {
      recordCheckpoint();
    }
  };

  // Derived display values
  const displayName = summary?.incidentName || incident?.incidentName || '';
  const displayLocation = summary?.location || incident?.location || '';
  const displayType = summary?.incidentType !== undefined ? summary.incidentType : incident?.incidentType;
  const displayStarted = summary?.startedAtUtc || incident?.startedAtUtc || '';
  const displayStatus = summary?.status ?? incident?.status;
  const isClosed = isIncidentClosed({ status: displayStatus });
  const { assignments } = useIncidentAssignments(isClosed ? incidentId : undefined);
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments,
  });
  const sc = summary ? deriveScopeCountsFromSummary(summary) : null;
  const total = sc?.accountabilityCounts.total ?? 0;
  const accountedCount = sc?.accountabilityCounts.accounted ?? summary?.accounted ?? 0;
  const notAccountedCount = sc?.accountabilityCounts.notAccounted ?? summary?.notAccounted ?? 0;
  const notCheckedCount = sc?.accountabilityCounts.notChecked ?? Math.max(0, total - accountedCount - notAccountedCount);
  const assignedOfficeNoBadge = sc?.assignedOfficeNoBadgeTodayCount ?? summary?.assignedOfficeNotBadgedIn ?? 0;
  const unsyncedChanges = summary?.unsyncedChanges ?? 0;
  const accountabilityPercent = total === 0 ? 0 : Math.round((accountedCount / total) * 100);

  const formatRelativeTime = (utc: string): string => {
    try {
      const diff = Date.now() - new Date(utc).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.floor(mins / 60);
      return `${hrs} hr ago`;
    } catch { return ''; }
  };

  const getActivityIcon = (action: string) => {
    const label = formatIncidentActivity(action);
    const a = label.toLowerCase();
    if (a.includes('accounted') && !a.includes('not')) return <AccountedIcon sx={{ fontSize: 18, color: appColors.status.success }} />;
    if (a.includes('not accounted')) return <NotAccountedIcon sx={{ fontSize: 18, color: appColors.status.error }} />;
    if (a.includes('not checked') || a.includes('reset')) return <NotCheckedIcon sx={{ fontSize: 18, color: appColors.text.caption }} />;
    if (a.includes('added') || a.includes('person')) return <AddedIcon sx={{ fontSize: 18, color: appColors.primary.main }} />;
    if (a.includes('checkpoint')) return <CheckIcon sx={{ fontSize: 18, color: appColors.status.success }} />;
    return <AccountedIcon sx={{ fontSize: 18, color: appColors.text.caption }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => loadData()}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  const PersonRow: React.FC<{ person: IncidentRosterPerson; color: string; tint: string }> = ({ person, color, tint }) => {
    const initials = person.displayName.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const parts: string[] = [];
    if (person.personType) parts.push(person.personType);
    if (person.floor) parts.push(`Floor ${person.floor}`);
    if (person.practice) parts.push(person.practice);
    const details = parts.join(' • ');
    const markedBy = person.lastUpdatedByName && person.lastUpdatedByName !== 'System'
      ? `Marked Not Accounted by ${person.lastUpdatedByName}${person.lastUpdatedAtUtc ? ` · ${formatRelativeTime(person.lastUpdatedAtUtc)}` : ''}`
      : person.lastUpdatedAtUtc ? `Updated ${formatRelativeTime(person.lastUpdatedAtUtc)}` : '';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25, px: 1, borderBottom: `1px solid ${appColors.border.light}`, bgcolor: tint, '&:last-child': { borderBottom: 'none' } }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: '0.75rem', fontWeight: 700, mr: 1.5 }}>{initials}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.3 }}>{person.displayName}</Typography>
            {person.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <PhoneIcon sx={{ fontSize: 13, color: appColors.text.placeholder }} />
                <Typography variant="caption" sx={{ color: appColors.text.caption, whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                  {person.phone}
                </Typography>
              </Box>
            )}
            {person.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                <EmailIcon sx={{ fontSize: 13, color: appColors.text.placeholder }} />
                <Typography variant="caption" sx={{ color: appColors.text.caption, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                  {person.email}
                </Typography>
              </Box>
            )}
          </Box>
          {details && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder, display: 'block', mt: 0.25 }}>
              {details}
            </Typography>
          )}
          {markedBy && (
            <Typography variant="caption" sx={{ color: appColors.status.error, display: 'block', mt: 0.25, fontWeight: 500 }}>
              {markedBy}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/incidents/${incidentId}/unaccounted`)}
          aria-label={`Review status for ${person.displayName}`}
          sx={{ color, borderColor: color, minWidth: 80, minHeight: 36, fontWeight: 600, '&:hover': { borderColor: color, bgcolor: `${color}0A` } }}
        >
          Review
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Header ── */}
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: displayName || 'Incident', path: `/incidents/${incidentId}/incidentdashboard` },
        { label: 'Headcount', path: `/incidents/${incidentId}/headcount` },
        { label: isClosed ? 'Summary' : 'Review Progress Checkpoint' },
      ]} />

      {/* ── Closed banner ── */}
      {isClosed && !closedRosterEditable && <ClosedIncidentBanner closedAtUtc={incident?.closedAtUtc} />}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate(isClosed ? `/incidents/${incidentId}/incidentdashboard` : `/incidents/${incidentId}/headcount`)}
            sx={{ color: appColors.text.body }}
          >
            {isClosed ? 'Back to Dashboard' : 'Continue Headcount'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {!isClosed && (
            <SyncStatusIndicator
              state={isOnline ? (unsyncedChanges > 0 ? 'saving' : 'online') : 'offline'}
              lastSyncedAt={lastUpdated}
            />
          )}
          {lastUpdated && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
              Updated {lastUpdated}
            </Typography>
          )}
          <IconButton size="small" onClick={() => loadData(false)} aria-label="Refresh data" sx={{ color: appColors.text.body }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Title row ── */}
      <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading, textAlign: 'center', mb: 0.5 }}>
        {isClosed ? 'Headcount Summary' : 'Review Progress Checkpoint'}
      </Typography>
      <Typography variant="body2" sx={{ color: appColors.text.caption, textAlign: 'center', mb: 1.5 }}>
        {isClosed
          ? 'This incident is closed. Checkpoints can no longer be recorded.'
          : 'Review current headcount status before recording a progress checkpoint.'}
      </Typography>

      {/* ── Incident Info ── */}
      {displayName && (
        <IncidentInfoBar
          incidentName={displayName}
          incidentType={displayType}
          location={displayLocation}
          startedAtUtc={displayStarted}
          status={displayStatus}
        />
      )}

      {/* ── Headcount Summary ── */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1 }}>
        Headcount Summary
      </Typography>
      <Box sx={{ mb: 2 }}>
        <ResponsiveKpiGrid>
          <KpiCard icon={<TotalIcon />} label="To Account" value={total} description="Accountability roster" variant="toAccount" />
          <KpiCard icon={<AccountedIcon />} label="Accounted" value={accountedCount}
            description={total > 0 ? `${accountabilityPercent}% of accountability roster` : 'People accounted'} variant="accounted" />
          <KpiCard icon={<NotAccountedIcon />} label="Not Accounted" value={notAccountedCount}
            description={notAccountedCount > 0 ? 'People not accounted' : 'Everyone accounted'} variant="notAccounted" neutralWhenZero />
          <StackedMiniKpi
            topLabel="Full Roster"
            topValue={sc?.fullRosterCounts.total ?? summary?.total ?? 0}
            bottomLabel="No Badge Today"
            bottomValue={sc?.assignedOfficeNoBadgeTodayNotChecked ?? assignedOfficeNoBadge}
          />
        </ResponsiveKpiGrid>
      </Box>

      {/* ── Compact sync status (replaces large Unsynced Changes card) ── */}
      {!isClosed && unsyncedChanges > 0 && (
        <Box aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SyncIcon sx={{ fontSize: 16, color: appColors.status.warning }} />
          <Typography variant="caption" sx={{ color: appColors.status.warning, fontWeight: 600 }}>
            Saving {unsyncedChanges} {unsyncedChanges === 1 ? 'change' : 'changes'}...
          </Typography>
        </Box>
      )}

      {/* ── Checkpoint readiness panel ── */}
      {!isClosed && (
        <Box
          role="region"
          aria-label="Checkpoint readiness"
          sx={{
            bgcolor: appColors.surface.card,
            border: `1px solid ${appColors.border.default}`,
            borderRadius: '12px',
            px: 2.5,
            py: 2,
            mb: 2.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5 }}>
            Checkpoint readiness
          </Typography>
          {assignedOfficeNoBadge > 0 && (
            <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1 }}>
              {assignedOfficeNoBadge} assigned-office / no-badge {assignedOfficeNoBadge === 1 ? 'person is' : 'people are'} outside the default accountability scope.
            </Typography>
          )}
          {notAccountedCount > 0 ? (
            <>
              <Typography variant="body2" sx={{ color: appColors.text.body, mb: 0.5 }}>
                {notAccountedCount} {notAccountedCount === 1 ? 'person is' : 'people are'} marked Not Accounted.{' '}
                {notCheckedCount > 0 && `${notCheckedCount} ${notCheckedCount === 1 ? 'person is' : 'people are'} still Not Checked.`}
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.5 }}>
                You can record a checkpoint, but review people marked Not Accounted before doing so.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  aria-label="Review Not Accounted people"
                  onClick={() => navigate(`/incidents/${incidentId}/unaccounted`)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', borderColor: appColors.status.error, color: appColors.status.error, '&:hover': { borderColor: appColors.status.error, bgcolor: `${appColors.status.error}0A` } }}
                >
                  Review Not Accounted
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/incidents/${incidentId}/headcount`)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                  Continue Headcount
                </Button>
                <Tooltip title={!canRecordCheckpoint ? 'You do not have permission to record checkpoints.' : unsyncedChanges > 0 ? 'Saving changes before checkpoint...' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!canRecordCheckpoint || recording || !isOnline || unsyncedChanges > 0}
                      onClick={handleRecordCheckpoint}
                      aria-label="Record Progress Checkpoint"
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                    >
                      Record Checkpoint
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          ) : notCheckedCount > 0 ? (
            <>
              <Typography variant="body2" sx={{ color: appColors.text.body, mb: 0.5 }}>
                No one is marked Not Accounted.{' '}
                {notCheckedCount} {notCheckedCount === 1 ? 'person is' : 'people are'} still Not Checked.
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.5 }}>
                You can record a checkpoint now, or continue headcount to check the remaining roster.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/incidents/${incidentId}/headcount`)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                  Continue Headcount
                </Button>
                <Tooltip title={!canRecordCheckpoint ? 'You do not have permission to record checkpoints.' : unsyncedChanges > 0 ? 'Saving changes before checkpoint...' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!canRecordCheckpoint || recording || !isOnline || unsyncedChanges > 0}
                      onClick={handleRecordCheckpoint}
                      aria-label="Record Progress Checkpoint"
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                    >
                      Record Checkpoint
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: appColors.status.success, mb: 1 }}>
                Everyone in the roster has been accounted for.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title={!canRecordCheckpoint ? 'You do not have permission to record checkpoints.' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!canRecordCheckpoint || recording || !isOnline || unsyncedChanges > 0}
                      onClick={handleRecordCheckpoint}
                      aria-label="Record Progress Checkpoint"
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                    >
                      Record Checkpoint
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/incidents/${incidentId}/incidentdashboard`)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                  Back to Dashboard
                </Button>
              </Box>
            </>
          )}
          {!canRecordCheckpoint && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: appColors.text.caption }}>
              You do not have permission to record checkpoints.
            </Typography>
          )}
        </Box>
      )}

      {/* ── Warning banner (calmer wording) ── */}
      {notAccountedCount > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
          bgcolor: `${appColors.status.error}14`, border: `1.5px solid ${appColors.status.error}60`, borderLeft: `4px solid ${appColors.status.error}`, borderRadius: '12px', px: 2.5, py: 2, mb: 2.5,
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <WarningIcon sx={{ color: appColors.status.error, fontSize: 22 }} />
              <Typography variant="body1" sx={{ fontWeight: 700, color: appColors.status.error }}>
                {notAccountedCount} {notAccountedCount === 1 ? 'person needs' : 'people need'} attention
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: appColors.text.body, ml: 4 }}>
              Review people marked Not Accounted before recording a checkpoint.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            aria-label="Review Not Accounted people"
            onClick={() => navigate(`/incidents/${incidentId}/unaccounted`)}
            sx={{ color: appColors.status.error, borderColor: appColors.status.error, minHeight: 36, fontWeight: 600, '&:hover': { borderColor: appColors.status.error, bgcolor: `${appColors.status.error}0A` } }}
          >
            Review Not Accounted
          </Button>
        </Box>
      )}

      {/* ── Two-panel row ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        {/* People Requiring Attention panel */}
        <Box sx={{ flex: '1 1 280px', minWidth: { xs: 0, sm: 280 }, bgcolor: appColors.surface.card, borderRadius: '12px', border: `1px solid ${appColors.border.default}`, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <NotAccountedIcon sx={{ fontSize: 20, color: appColors.status.error }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.status.error }}>
                People Requiring Attention ({notAccountedCount})
              </Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate(`/incidents/${incidentId}/unaccounted`)}
              sx={{ color: appColors.primary.main, fontWeight: 600, fontSize: '0.75rem' }}
            >
              View All
            </Button>
          </Box>
          <Typography variant="caption" sx={{ px: 2, color: appColors.text.caption, display: 'block', mb: 1 }}>
            Review each person and update their status in Headcount or Not Accounted.
          </Typography>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {notAccountedPeople.length === 0 ? (
              <Typography variant="body2" sx={{ color: appColors.text.placeholder, textAlign: 'center', py: 2 }}>
                No people require attention.
              </Typography>
            ) : (
              notAccountedPeople.slice(0, 5).map((p) => <PersonRow key={p.personId} person={p} color={appColors.status.error} tint={`${appColors.status.error}06`} />)
            )}
          </Box>
          {notAccountedPeople.length > 0 && (
            <Box
              onClick={() => navigate(`/incidents/${incidentId}/unaccounted`)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: `1px solid ${appColors.border.default}`, px: 2, py: 1, cursor: 'pointer',
                '&:hover': { bgcolor: appColors.surface.page },
              }}
            >
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                Showing first {Math.min(5, notAccountedPeople.length)} of {notAccountedCount}
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 18, color: appColors.text.caption }} />
            </Box>
          )}
        </Box>

        {/* Recent Changes panel */}
        <Box sx={{ flex: '1 1 280px', minWidth: { xs: 0, sm: 280 }, bgcolor: appColors.surface.card, borderRadius: '12px', border: `1px solid ${appColors.border.default}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
              Recent Changes
            </Typography>
            <Button
              variant="text"
              size="small"
              endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate(`/incidents/${incidentId}/incidentdashboard`)}
              sx={{ color: appColors.primary.main, fontWeight: 600, fontSize: '0.75rem' }}
            >
              View All
            </Button>
          </Box>
          <Box sx={{ flex: 1, px: 1.5, pb: 1.5 }}>
            {recentActivity.length === 0 ? (
              <Typography variant="body2" sx={{ color: appColors.text.placeholder, textAlign: 'center', py: 2 }}>
                No recent changes.
              </Typography>
            ) : (
              recentActivity.slice(0, 4).map((a, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, px: 0.5, borderBottom: idx < 3 ? `1px solid ${appColors.border.light}` : 'none' }}>
                  {getActivityIcon(a.action)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.3 }}>{a.personName}</Typography>
                    <Typography variant="caption" sx={{ color: appColors.text.caption }}>{formatIncidentActivity(a.action)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ color: appColors.text.placeholder, display: 'block' }}>{a.timestamp ? formatRelativeTime(a.timestamp) : ''}</Typography>
                    {a.updatedBy && <Typography variant="caption" sx={{ color: appColors.text.caption }}>by {a.updatedBy}</Typography>}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Compact realtime collaboration strip ── */}
      {!isClosed && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: appColors.primary.light, border: `1px solid ${appColors.border.default}`, borderRadius: '8px',
          px: 2, py: 1, mb: 2.5,
        }}>
          <WifiIcon sx={{ fontSize: 16, color: isOnline ? appColors.status.success : appColors.text.caption }} />
          <Typography variant="caption" sx={{ color: appColors.text.body }}>
            {isOnline
              ? 'Realtime collaboration active. Other headcounters can see your synced changes.'
              : 'Realtime unavailable. Refreshing periodically.'}
          </Typography>
        </Box>
      )}

      {/* ── Record error ── */}
      {recordError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setRecordError(null)}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={() => { setRecordError(null); handleRecordCheckpoint(); }}>Retry</Button>
              <Button color="inherit" size="small" onClick={() => navigate(`/incidents/${incidentId}/headcount`)}>Continue Headcount</Button>
            </Box>
          }
        >
          Unable to record checkpoint. Your current headcount changes were not recorded as a checkpoint. Please try again.
        </Alert>
      )}

      {/* ── Bottom action bar ── */}
      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'center', gap: 2,
        bgcolor: appColors.surface.card, border: `1px solid ${appColors.border.default}`, borderRadius: '12px',
        px: { xs: 2, sm: 3 }, py: 2,
      }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate(isClosed ? `/incidents/${incidentId}/incidentdashboard` : `/incidents/${incidentId}/headcount`)}
          sx={{
            flex: 1, maxWidth: 340, minHeight: 48,
            color: appColors.text.heading, borderColor: appColors.border.subtle,
          }}
        >
          {isClosed ? '← Back to Dashboard' : '← Continue Headcount'}
        </Button>
        {!isClosed && (
          <Tooltip title={!canRecordCheckpoint ? 'You do not have permission to record checkpoints.' : unsyncedChanges > 0 ? 'Saving changes before checkpoint...' : ''}>
            <span style={{ flex: 1, maxWidth: 340 }}>
              <Button
                variant="contained"
                startIcon={recording ? <CircularProgress size={18} color="inherit" /> : <SubmitIcon />}
                onClick={handleRecordCheckpoint}
                disabled={recording || !isOnline || !canRecordCheckpoint || unsyncedChanges > 0}
                aria-label="Record Progress Checkpoint"
                sx={{ width: '100%', minHeight: 48 }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>
                    Record Progress Checkpoint
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.85 }}>
                    Save current accountability snapshot — incident remains open.
                  </Typography>
                </Box>
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* ── Confirmation Dialog ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="checkpoint-confirm-title"
        slotProps={{ paper: { sx: { borderRadius: '12px', maxWidth: 420 } } }}
      >
        <DialogTitle id="checkpoint-confirm-title" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Record progress checkpoint?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: appColors.text.body }}>
            {notAccountedCount > 0 && (
              <>{notAccountedCount} {notAccountedCount === 1 ? 'person is' : 'people are'} marked Not Accounted.<br /></>
            )}
            {notCheckedCount > 0 && (
              <>{notCheckedCount} {notCheckedCount === 1 ? 'person is' : 'people are'} still Not Checked.<br /></>
            )}
            This checkpoint will record {accountedCount}/{total} accounted. The incident will remain open.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{ color: appColors.text.heading, borderColor: appColors.border.subtle }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setConfirmOpen(false); recordCheckpoint(); }}
            variant="contained"
            autoFocus
          >
            Record Checkpoint
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success snackbar ── */}
      <Snackbar
        open={recordSuccess}
        autoHideDuration={3000}
        onClose={() => setRecordSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setRecordSuccess(false)} sx={{ width: '100%' }}>
          Checkpoint recorded: {accountedCount}/{total} accounted. The incident remains open.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewSubmitPage;
